// Whether this browser has agreed to send us its games.
//
// **Opt-in, and the default is not "no" — it is "not asked".** Three states,
// because two would make a player who has never seen the question
// indistinguishable from one who declined, and those want opposite behaviour:
// one gets asked, the other must never be asked again.
//
// **This is a browser, not a person.** There are no accounts here — being
// "authenticated" means holding the `fc_session` cookie earned by the one
// `SITE_PASSWORD`, which every playtester shares. So consent is recorded per
// browser and per disclosure version, and a submission carries a random id
// that identifies a *browser across its own games* and nothing else. It is
// deliberately not derived from anything: not the seat name, not the room, not
// the password. A random 128 bits is the whole of it, and clearing it is
// clearing site data.
//
// **Nothing in `src/engine/rules/` or `src/engine/ai/` may read any of this.** Two clients
// derive one board from one log, so a rule that consulted whether somebody
// agreed to telemetry would derive two different games from the same log —
// the reason `src/engine/telemetry/profile.ts` already states, applied to a second
// thing a browser knows and the game must not.

/**
 * What the player was told when they answered.
 *
 * A disclosure is a promise about what leaves the machine, so agreeing to one
 * version is not agreeing to the next. Bump this when what is collected
 * changes, and everybody is asked again — which is the point, and is why it is
 * a number rather than a boolean: an old `true` must not be readable as
 * agreement to something they were never shown.
 */
export const CONSENT_VERSION = 1;

/** Not asked yet, agreed, or declined. Three states on purpose. */
export type ConsentAnswer = "unasked" | "agreed" | "declined";

export interface Consent {
  /** The disclosure version this answer was given against. */
  version: number;
  answer: ConsentAnswer;
  /** When they answered, ms since epoch. Absent while unasked. */
  at?: number;
  /**
   * A random id for this browser, minted when consent is *given* and thrown
   * away when it is withdrawn.
   *
   * Minted late rather than at first load, so a browser that never agrees
   * never has one to send. Kept across games, because the questions worth
   * asking of playtest data are mostly "did the same person come back".
   */
  id?: string;
}

export const STORAGE_KEY = "fc.telemetry.consent";

export const unaskedConsent = (): Consent => ({ version: CONSENT_VERSION, answer: "unasked" });

/**
 * May we send a game from this browser?
 *
 * **Asks the version as well as the answer**, so a stale agreement is not
 * consent. A stored `agreed` at an older version reads as `unasked` on the way
 * out (`readConsent`), which is what re-opens the question.
 */
export const maySubmit = (consent: Consent): boolean =>
  consent.answer === "agreed" && consent.version === CONSENT_VERSION && Boolean(consent.id);

/** Should the question be put to this browser? */
export const shouldAsk = (consent: Consent): boolean => consent.answer === "unasked";

/**
 * Mint a browser id.
 *
 * `crypto.randomUUID` where it exists — every browser this ships to has it over
 * https, and the fallback is for http on a LAN address and for jsdom. **Not
 * seeded and not derived**: this file is outside `src/engine/rules/` and `src/engine/ai/`
 * precisely so an unpredictable value is allowed here, the same exemption
 * `ui/playerName.ts` and `freshSeed` take.
 */
function mintId(): string {
  const api = globalThis.crypto;
  if (api && typeof api.randomUUID === "function") return api.randomUUID();
  if (api && typeof api.getRandomValues === "function") {
    const bytes = api.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Record an answer.
 *
 * **Declining destroys the id.** Withdrawing consent and then giving it again
 * mints a new one, so the two stretches of play cannot be joined up — which is
 * what withdrawing ought to mean, rather than "pause the uploads and keep the
 * name".
 */
export function answerConsent(previous: Consent, agreed: boolean): Consent {
  if (!agreed) return { version: CONSENT_VERSION, answer: "declined", at: Date.now() };
  return {
    version: CONSENT_VERSION,
    answer: "agreed",
    at: Date.now(),
    // Kept when it is already there and the version has not moved, so
    // re-agreeing to the *same* disclosure is not a new browser.
    id: previous.answer === "agreed" && previous.version === CONSENT_VERSION && previous.id
      ? previous.id
      : mintId()
  };
}

/**
 * Read the stored answer.
 *
 * **Anything unreadable is `unasked`, never `agreed`.** A corrupt record, a
 * record from a future version, an answer given against an older disclosure —
 * all of them mean we do not have consent, and the failure has to fall that
 * way rather than toward sending. `profile.ts` makes the same argument about a
 * store the player owns; here being wrong sends somebody's game somewhere they
 * did not agree to, so the reading is stricter.
 */
export function readConsent(storage?: Storage): Consent {
  const store = storage ?? safeStorage();
  if (!store) return unaskedConsent();
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return unaskedConsent();
    const parsed = JSON.parse(raw) as Partial<Consent> | null;
    if (!parsed || typeof parsed !== "object") return unaskedConsent();
    if (parsed.version !== CONSENT_VERSION) return unaskedConsent();
    if (parsed.answer !== "agreed" && parsed.answer !== "declined") return unaskedConsent();
    if (parsed.answer === "agreed" && typeof parsed.id !== "string") return unaskedConsent();
    return {
      version: CONSENT_VERSION,
      answer: parsed.answer,
      at: typeof parsed.at === "number" ? parsed.at : undefined,
      id: parsed.answer === "agreed" ? parsed.id : undefined
    };
  } catch {
    return unaskedConsent();
  }
}

export function writeConsent(consent: Consent, storage?: Storage): void {
  const store = storage ?? safeStorage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // A full or disabled store is not worth breaking a game over. The cost is
    // being asked again next time, which is the safe direction.
  }
}

/** `localStorage` throws outright in some privacy modes, so it is asked for once. */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
